<template>
    <!--
      번역 탭 — 문항에서 번역할 수 있는 문자열을 트리 순서대로 한 표에 놓는다.
      한국어는 읽기 전용(편집 탭에서 고친다), 영/중은 여기서 친다.
      저장은 구 화면과 같은 API 두 갈래(meta / sub_data_contents)다. 트리 쪽 값은 노드의
      번역 객체를 직접 가리키므로 toV1 이 그대로 실어 나간다 — 배열은 배열로 남는다.
    -->
    <div class="i18n">
        <div class="bar">
            <label class="cb"><input v-model="onlyMissing" type="checkbox"><span>빈 칸만 보기</span></label>
            <span class="stat">전체 {{ rows.length }}줄 · 빈 칸 <b :class="{warn: missing > 0}">{{ missing }}</b></span>
            <span class="grow"></span>
            <button class="btn tiny" :disabled="busy || !sub || !sub.sub_code" title="제목·설명·안내를 자동 번역합니다 (기존 수정본 덮어씀)" @click="auto('meta')">자동 번역 — 제목·설명</button>
            <button class="btn tiny" :disabled="busy || !root" title="문항 내용(선택지·표·안내)을 자동 번역합니다 (기존 수정본 덮어씀)" @click="auto('sub_data_contents')">자동 번역 — 문항 내용</button>
        </div>

        <p v-if="!rows.length" class="h">번역할 문자열이 없습니다. 편집 탭에서 문항을 먼저 채우세요.</p>

        <div v-for="g in groups" :key="g.where" class="grp">
            <p class="gh">{{ g.where }}</p>
            <div class="tw">
                <table class="tt">
                    <thead>
                        <tr>
                            <th class="kind">종류</th>
                            <th class="ko">한국어 <span class="ro">읽기 전용</span></th>
                            <th v-for="l in LOCALES" :key="l.key">{{ l.label }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="r in g.rows" :key="r.id" :class="{miss: isMissing(r)}">
                            <td class="kind"><span class="badge" :class="r.scope">{{ r.kind }}</span></td>
                            <td class="ko">
                                <span class="kotext">{{ r.ko }}</span>
                                <code v-if="r.key" class="skey" title="저장 키 — 채점식과 이미 제출된 답변이 이 값을 씁니다">{{ r.key }}</code>
                            </td>
                            <td v-for="l in LOCALES" :key="l.key" class="in">
                                <textarea v-if="isLong(r)" :value="r.get(l.key)" rows="3" :disabled="busy"
                                          :placeholder="r.get(l.key) ? '' : '(비어 있음)'" :class="{empty: !r.get(l.key)}"
                                          @input="r.set(l.key, $event.target.value, $set); touch()"></textarea>
                                <input v-else type="text" :value="r.get(l.key)" :disabled="busy" :placeholder="r.get(l.key) ? '' : '(비어 있음)'"
                                       :class="{empty: !r.get(l.key)}"
                                       @input="r.set(l.key, $event.target.value, $set); touch()">
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <p class="h note">
            <b>저장 방식</b> — 제목·설명·안내·해당없음 라벨은 <code class="mono">translation_part=meta</code>, 나머지는
            문항 내용(<code class="mono">sub_data_contents</code>)에 실려 통째로 나갑니다. 선택지·표 머리글·드롭다운 번역은
            <b>배열이 배열로</b> 저장되므로 구 화면처럼 쉼표로 뭉개지지 않습니다.
        </p>

        <details class="payload">
            <summary>저장될 형태 — <code class="mono">POST /api/translate/subs_init/{{ subCode }}</code> 두 번</summary>
            <pre class="code mono">{{ payloadPreview }}</pre>
        </details>
        <div class="save">
            <span v-if="savedAt" class="h" style="margin:0">{{ savedAt }}</span>
            <button class="btn tiny primary" :disabled="busy || !rows.length" @click="save">{{ busy ? '저장 중…' : '번역 저장' }}</button>
        </div>
    </div>
</template>

<script>
import {toV1} from '@core_utils/subComponent/structure-tree.js';
import {i18nRowsOf, missingCount, metaPayloadOf, LOCALES} from '@core_utils/subComponent/i18n-rows.js';

export default {
    name: 'Sv2I18n',
    props: {
        root: {type: Object, default: null},
        sub: {type: Object, default: null},        // loadedSub — 메타 번역(_e/_c)은 여기에 직접 쓴다
        subCode: {type: String, default: ''},
        busy: {type: Boolean, default: false}      // 부모(Sv2Editor)가 저장·자동 번역 중이면 입력과 버튼을 잠근다
    },
    data() { return {onlyMissing: false, tick: 0, savedAt: '', LOCALES}; },
    computed: {
        rows() { void this.tick; return i18nRowsOf(this.root, this.sub); },
        missing() { void this.tick; return missingCount(this.rows); },
        visible() { return this.onlyMissing ? this.rows.filter((r) => this.isMissing(r)) : this.rows; },
        groups() {
            const out = []; const by = {};
            this.visible.forEach((r) => { if (!by[r.where]) { by[r.where] = {where: r.where, rows: []}; out.push(by[r.where]); } by[r.where].rows.push(r); });
            return out;
        },
        payloadPreview() {
            const meta = metaPayloadOf(this.sub);
            let contents = null;
            try { contents = this.root ? toV1(this.root) : null; } catch (e) { contents = {error: String(e)}; }
            return JSON.stringify({
                '1. translation_part=meta': {sub: meta},
                '2. translation_part=sub_data_contents': {sub: {sub_code: this.subCode, sub_data_contents: contents}}
            }, null, 2).slice(0, 4000);
        }
    },
    watch: {
        missing: {immediate: true, handler(n) { this.$emit('missing', n); }}
    },
    methods: {
        isMissing(r) { return LOCALES.some((l) => !r.get(l.key)); },
        /** 긴 글·줄바꿈·HTML 이 든 항목은 여러 줄로 — 한 줄 입력으로는 다루기 어렵다 */
        isLong(r) {
            const ko = String(r.ko || '');
            return ko.length > 60 || /[\r\n<]/.test(ko);
        },
        touch() { this.tick++; this.$emit('touch'); },   // rows 재계산 + 부모에 '저장 안 한 변경' 신호
        // 덮어쓰기 확인은 부모가 한다 — 저장하지 않은 구조·채점 변경 건수까지 알아야 해서(Sv2Editor.autoTranslate)
        auto(part) { this.$emit('auto', part); },
        save() {
            this.savedAt = '';
            this.$emit('save', {meta: metaPayloadOf(this.sub), contents: this.root ? toV1(this.root) : null});
        }
    }
};
</script>

<style scoped>
.i18n{display:flex;flex-direction:column;gap:.75rem}
.mono{font-family:Consolas,monospace}
.h{font-size:.76rem;color:#6b747e;margin:0;line-height:1.5}
.bar{display:flex;align-items:center;gap:.7rem;flex-wrap:wrap}
.bar .grow{flex:1}
.stat{font-size:.8rem;color:#6b747e} .stat b{color:#2c6a4a} .stat b.warn{color:#9e2f24}
.cb{display:flex;align-items:center;gap:.35rem;font-size:.8rem;color:#3d454e;cursor:pointer}
.grp{display:flex;flex-direction:column;gap:.3rem}
.gh{margin:.2rem 0 0;font-size:.78rem;font-weight:700;color:#2f6b8f}
.tw{overflow-x:auto;border:1px solid #dde2e9;border-radius:6px;background:#fff}
.tt{border-collapse:collapse;width:100%;font-size:.84rem;min-width:40rem}
.tt th,.tt td{padding:.35rem .5rem;border-bottom:1px solid #eef1f3;vertical-align:top;text-align:left}
.tt thead th{background:#f8f9fb;font-size:.72rem;color:#6b747e;font-weight:600}
.tt tbody tr:last-child td{border-bottom:0}
.tt tr.miss td{background:#fffdf5}
.tt th.kind,.tt td.kind{width:8rem;white-space:nowrap}
.tt th.ko,.tt td.ko{width:30%}
.ro{font-weight:400;font-size:.68rem;color:#8b95a3;margin-left:.3rem}
.kotext{color:#3d454e;overflow-wrap:anywhere;line-height:1.45}
.badge{display:inline-block;font-size:.68rem;padding:.06rem .45rem;border-radius:999px;border:1px solid #dde2e9;color:#3d454e;background:#fff;white-space:nowrap}
.badge.meta{border-color:#c9dcdf;color:#1f5f6b;background:#e6f0f6}
td.in input,td.in textarea{width:100%;border:1px solid #dde2e9;border-radius:5px;padding:.28rem .45rem;font:inherit;font-size:.84rem;background:#fff}
td.in textarea{resize:vertical;line-height:1.5}
td.in input.empty,td.in textarea.empty{border-style:dashed}
.skey{display:inline-block;margin-left:.3rem;font-family:Consolas,monospace;font-size:.7rem;color:#8a6212;
  background:#fbf3e2;border:1px solid #ecdcb8;border-radius:3px;padding:.02rem .3rem;cursor:help}
td.in input:focus,td.in textarea:focus{outline:2px solid #2f6b8f;outline-offset:-1px}
.note{background:#f8f9fb;border:1px dashed #dde2e9;border-radius:5px;padding:.4rem .6rem}
.code{margin:.3rem 0 0;padding:.55rem .7rem;background:#1c2126;color:#d8dee5;border-radius:6px;font-size:.74rem;line-height:1.55;white-space:pre-wrap;overflow-wrap:anywhere}
.payload summary{font-size:.78rem;color:#6b747e;cursor:pointer}
.save{display:flex;gap:.5rem;align-items:center;justify-content:flex-end;border-top:1px solid #eaeef3;padding-top:.5rem}
.btn{border:1px solid #dde2e9;background:#fff;border-radius:6px;padding:.35rem .8rem;font:inherit;cursor:pointer;color:#3d454e}
.btn.tiny{font-size:.78rem;padding:.25rem .6rem}
.btn:disabled{opacity:.5;cursor:not-allowed}
.btn.primary{background:#2f6b8f;border-color:#2f6b8f;color:#fff}
</style>
